import React, { useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useInternalNode,
  Node,
  Edge,
  NodeProps,
  EdgeProps,
  InternalNode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

/*
 * ArchFlow — 재사용 가능한 인터랙티브 시스템 구성도 컴포넌트
 *
 * 기존 Mermaid `graph TB/LR` 다이어그램을 코드 정의 + pan/zoom/드래그 가능한
 * React Flow 노드맵으로 전환하기 위한 공용 컴포넌트.
 *
 *  - group : 라벨이 붙은 컨테이너 박스 (Mermaid subgraph 대응)
 *  - chip  : 개별 구성요소 노드 (이모지 + 제목 + 부제)
 *  - edge  : floating bezier (핸들 위치와 무관하게 박스 경계 자동 연결)
 */

// ── 색상 팔레트 (기존 가이드 색감과 동일 계열) ──
export type Tone =
  | 'indigo' | 'green' | 'orange' | 'blue'
  | 'purple' | 'red' | 'amber' | 'slate' | 'cyan';

const PALETTE: Record<Tone, { bg: string; border: string; text: string; groupBg: string }> = {
  indigo: { bg: '#eef2ff', border: '#4f46e5', text: '#3730a3', groupBg: 'rgba(79,70,229,0.04)' },
  green:  { bg: '#ecfdf5', border: '#10b981', text: '#047857', groupBg: 'rgba(16,185,129,0.05)' },
  orange: { bg: '#fff7ed', border: '#ea580c', text: '#c2410c', groupBg: 'rgba(234,88,12,0.05)' },
  blue:   { bg: '#eff6ff', border: '#2563eb', text: '#1d4ed8', groupBg: 'rgba(37,99,235,0.05)' },
  purple: { bg: '#faf5ff', border: '#7c3aed', text: '#6d28d9', groupBg: 'rgba(124,58,237,0.05)' },
  red:    { bg: '#fef2f2', border: '#dc2626', text: '#b91c1c', groupBg: 'rgba(220,38,38,0.05)' },
  amber:  { bg: '#fffbeb', border: '#f59e0b', text: '#b45309', groupBg: 'rgba(245,158,11,0.06)' },
  slate:  { bg: '#f8fafc', border: '#94a3b8', text: '#475569', groupBg: 'rgba(148,163,184,0.06)' },
  cyan:   { bg: '#ecfeff', border: '#06b6d4', text: '#0e7490', groupBg: 'rgba(6,182,212,0.06)' },
};

// ── 공개 데이터 타입 ──
export interface ArchGroup {
  id: string;
  label: string;
  x: number; y: number; w: number; h: number;
  tone?: Tone;
}
export interface ArchNode {
  id: string;
  label: string;
  sub?: string;
  emoji?: string;
  x: number; y: number;
  w?: number;
  group?: string;     // 부모 group id (있으면 좌표는 group 기준 상대값)
  tone?: Tone;
}
export interface ArchEdge {
  source: string;
  target: string;
  label?: string;
  animated?: boolean;
  dashed?: boolean;
  tone?: Tone;
}

export interface ArchFlowProps {
  groups?: ArchGroup[];
  nodes: ArchNode[];
  edges: ArchEdge[];
  height?: number;
}

// ── 커스텀 노드: 그룹 컨테이너 ──
const GroupNode = ({ data }: NodeProps) => {
  const tone = (data as any).tone as Tone;
  const c = PALETTE[tone] || PALETTE.slate;
  return (
    <div
      style={{
        width: '100%', height: '100%',
        background: c.groupBg,
        border: `1.5px dashed ${c.border}`,
        borderRadius: 14,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          position: 'absolute', top: -11, left: 14,
          background: '#fff', padding: '1px 10px',
          fontSize: 12, fontWeight: 700, color: c.text,
          border: `1px solid ${c.border}`, borderRadius: 8,
          whiteSpace: 'nowrap',
        }}
      >
        {(data as any).label}
      </div>
    </div>
  );
};

// ── 커스텀 노드: 구성요소 칩 ──
const ChipNode = ({ data }: NodeProps) => {
  const tone = (data as any).tone as Tone;
  const c = PALETTE[tone] || PALETTE.indigo;
  const sub = (data as any).sub as string | undefined;
  const emoji = (data as any).emoji as string | undefined;
  return (
    <div
      style={{
        background: c.bg,
        border: `1.5px solid ${c.border}`,
        borderRadius: 10,
        padding: '8px 12px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        minWidth: 90,
        lineHeight: 1.35,
      }}
    >
      {/* floating 엣지용 히든 핸들 (위치 무관) */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>
        {emoji ? `${emoji} ` : ''}{(data as any).label}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2, whiteSpace: 'pre-line' }}>{sub}</div>}
    </div>
  );
};

// ── floating edge 기하 계산 (React Flow 공식 패턴) ──
function getNodeIntersection(intersectionNode: InternalNode, targetNode: InternalNode) {
  const w = (intersectionNode.measured?.width ?? 0) / 2;
  const h = (intersectionNode.measured?.height ?? 0) / 2;
  const ip = intersectionNode.internals.positionAbsolute;
  const tp = targetNode.internals.positionAbsolute;
  const x2 = ip.x + w;
  const y2 = ip.y + h;
  const x1 = tp.x + (targetNode.measured?.width ?? 0) / 2;
  const y1 = tp.y + (targetNode.measured?.height ?? 0) / 2;
  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;
  return { x, y };
}

const FloatingEdge = ({ id, source, target, markerEnd, style, data }: EdgeProps) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;
  const sp = getNodeIntersection(sourceNode, targetNode);
  const tp = getNodeIntersection(targetNode, sourceNode);
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX: sp.x, sourceY: sp.y,
    targetX: tp.x, targetY: tp.y,
  });
  const label = (data as any)?.label as string | undefined;
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
              background: '#fff', padding: '1px 6px', borderRadius: 6,
              fontSize: 10.5, color: '#475569', border: '1px solid #e2e8f0',
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const nodeTypes = { group: GroupNode, chip: ChipNode };
const edgeTypes = { floating: FloatingEdge };

const Inner: React.FC<ArchFlowProps> = ({ groups = [], nodes, edges, height = 460 }) => {
  const rfNodes: Node[] = useMemo(() => {
    const g: Node[] = groups.map((gr) => ({
      id: gr.id,
      type: 'group',
      position: { x: gr.x, y: gr.y },
      data: { label: gr.label, tone: gr.tone },
      style: { width: gr.w, height: gr.h },
      selectable: false,
      draggable: false,
      zIndex: 0,
    }));
    const n: Node[] = nodes.map((nd) => ({
      id: nd.id,
      type: 'chip',
      position: { x: nd.x, y: nd.y },
      data: { label: nd.label, sub: nd.sub, emoji: nd.emoji, tone: nd.tone },
      ...(nd.group ? { parentId: nd.group, extent: 'parent' as const } : {}),
      ...(nd.w ? { style: { width: nd.w } } : {}),
      zIndex: 1,
    }));
    return [...g, ...n];
  }, [groups, nodes]);

  const rfEdges: Edge[] = useMemo(
    () =>
      edges.map((e, i) => {
        const c = e.tone ? PALETTE[e.tone] : null;
        const stroke = c ? c.border : '#94a3b8';
        return {
          id: `e${i}-${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          type: 'floating',
          animated: e.animated,
          data: { label: e.label },
          markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 16, height: 16 },
          style: { stroke, strokeWidth: 1.6, strokeDasharray: e.dashed ? '5 4' : undefined },
        } as Edge;
      }),
    [edges],
  );

  return (
    <div style={{ height, width: '100%', background: '#fff', border: '1px solid #eef2f6', borderRadius: 16 }}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
      >
        <Background color="#f1f5f9" gap={18} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

const ArchFlow: React.FC<ArchFlowProps> = (props) => (
  <ReactFlowProvider>
    <Inner {...props} />
  </ReactFlowProvider>
);

export default ArchFlow;
